import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  CardHeadeWithActions,
  MwButton,
  MwInputText,
  MwSpinnerButton,
} from "../components/ui";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import axios from "axios";
import { useTenant } from "../hooks";
import * as XLSX from "xlsx";
import SelectorTree from "../components/ui/SelectAccount/SelectorTree";
import AccountContext from "../utils/AccountsHelper";

const EMPTY_VALUE = "---";
const EMPTY_VALIDATION = { field: "", message: "" };
const EXCEL_NUMBER_FORMAT = "#,##0.00;(#,##0.00)";

const formatDateForInput = (date) => {
  const safeDate = new Date(date);
  safeDate.setMinutes(safeDate.getMinutes() - safeDate.getTimezoneOffset());
  return safeDate.toISOString().split("T")[0];
};

const toNumber = (value) => Number(value) || 0;

const createInitialState = () => {
  const now = new Date();
  const currentYear = now.getFullYear();

  return {
    date_from: formatDateForInput(new Date(currentYear, 0, 1)),
    date_to: formatDateForInput(new Date(currentYear, 11, 31)),
    account_no: "",
    accountLabel: "",
  };
};

const formReducer = (state, action) => {
  switch (action.type) {
    case "ADD_TO_FORM":
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };
    default:
      return state;
  }
};

const walkAccounts = (nodes, visitor) => {
  (Array.isArray(nodes) ? nodes : []).forEach((node) => {
    visitor(node);

    if (Array.isArray(node?.childs) && node.childs.length > 0) {
      walkAccounts(node.childs, visitor);
    }
  });
};

const getAccountNumber = (account) =>
  String(
    account?.account_no || account?.accountNumber || account?.value || "",
  ).trim();

const getAccountName = (account, isArabic) => {
  if (!account) return "";

  if (isArabic) {
    return (
      account?.name_ar ||
      account?.accountNameAR ||
      account?.name_en ||
      account?.accountNameEN ||
      account?.name ||
      ""
    );
  }

  return (
    account?.name_en ||
    account?.accountNameEN ||
    account?.name_ar ||
    account?.accountNameAR ||
    account?.name ||
    ""
  );
};

const getLocalizedText = (value, isArabic) => {
  if (Array.isArray(value)) {
    return (
      value.find((item) => item?.lang === (isArabic ? "ar" : "en"))?.text ||
      value.find((item) => item?.lang === "ar")?.text ||
      value.find((item) => item?.lang === "en")?.text ||
      value[0]?.text ||
      ""
    );
  }
  if (typeof value === "object" && value !== null) {
    return String(value?.text || "");
  }

  return String(value || "");
};

const getReportPayload = (responseData) => {
  if (
    responseData?.data?.data &&
    typeof responseData.data.data === "object" &&
    !Array.isArray(responseData.data.data)
  ) {
    return responseData.data.data;
  }

  if (
    responseData?.data &&
    typeof responseData.data === "object" &&
    !Array.isArray(responseData.data)
  ) {
    return responseData.data;
  }

  if (
    responseData &&
    typeof responseData === "object" &&
    !Array.isArray(responseData)
  ) {
    return responseData;
  }

  return {};
};

const getReportTransactions = (responseData) => {
  const reportPayload = getReportPayload(responseData);

  if (Array.isArray(reportPayload?.transactions)) {
    return reportPayload.transactions;
  }

  if (
    reportPayload?.transactions &&
    typeof reportPayload.transactions === "object"
  ) {
    return [reportPayload.transactions];
  }

  return [];
};

const flattenTransactionDetails = (transactions, accountNumber) => {
  const normalizedAccountNumber = String(accountNumber || "").trim();

  return (Array.isArray(transactions) ? transactions : []).flatMap(
    (transaction, transactionIndex) => {
      const transactionAccountNumber = String(
        transaction?.account_no || "",
      ).trim();
      const details = Array.isArray(transaction?.detail_relation)
        ? transaction.detail_relation
        : [];

      if (details.length === 0) {
        if (
          normalizedAccountNumber &&
          transactionAccountNumber &&
          transactionAccountNumber !== normalizedAccountNumber
        ) {
          return [];
        }

        const debit = toNumber(transaction?.trans_de);
        const credit = toNumber(transaction?.trans_cr);
        const runningBalance =
          transaction?.balance ??
          transaction?.movement_balance ??
          transaction?.running_balance ??
          debit - credit;

        return [
          {
            id:
              transaction?.id ||
              `${transaction?.trans_no || "transaction"}-${transactionIndex}`,
            serial: transaction?.trans_no || EMPTY_VALUE,
            tr_date: transaction?.trans_date_m || EMPTY_VALUE,
            description:
              transaction?.trans_desc ||
              transaction?.trans_notes ||
              transaction?.description ||
              EMPTY_VALUE,
            balance_de: debit,
            balance_cr: credit,
            movement_balance: toNumber(runningBalance),
          },
        ];
      }

      return details
        .filter(
          (detail) =>
            String(detail?.account_no || "").trim() === normalizedAccountNumber,
        )
        .map((detail, detailIndex) => {
          const debit = toNumber(detail?.trans_de);
          const credit = toNumber(detail?.trans_cr);
          const movementBalance =
            detail?.balance ??
            transaction?.balance ??
            detail?.movement_balance ??
            Math.abs(debit - credit);

          return {
            id:
              detail?.id ||
              `${transaction?.id || transactionIndex}-${detailIndex}-${detail?.trans_no || transaction?.trans_no || ""}`,
            serial: detail?.trans_no || EMPTY_VALUE,
            tr_date: transaction?.trans_date_m || EMPTY_VALUE,
            description:
              detail?.trans_notes ||
              detail?.trans_desc ||
              transaction?.trans_desc ||
              EMPTY_VALUE,
            balance_de: debit,
            balance_cr: credit,
            movement_balance: toNumber(movementBalance),
          };
        });
    },
  );
};

export const AccountStatement = () => {
  const { t } = useTranslation("global");
  const tableRef = useRef(null);
  const { accounts } = useContext(AccountContext);
  const { tenantId, branchSelected, companySelected, getCompanyById } =
    useTenant();
  const [openingBalance, setOpeningBalance] = useState("0");
  const [selectedP, setSelectedP] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [validationError, setValidationError] = useState(EMPTY_VALIDATION);
  const [state, dispatch] = useReducer(
    formReducer,
    undefined,
    createInitialState,
  );
  const [data, setData] = useState([]);

  const isArabic = i18next.dir(i18next.language) === "rtl";
  const reportTitle = isArabic
    ? t("reportsList.accountStatement.titleAr")
    : t("reportsList.accountStatement.titleEn");
  const exportBaseName =
    reportTitle.trim().replace(/\s+/g, "_") || "AccountStatement";
  const transactionDateLabel = t("JournalEntry.date");
  const serialLabel = t("JournalEntry.numofentry");
  const descriptionLabel = t("JournalEntry.notes");
  const balanceLabel = t("reports.balance");

  const currentCompany = companySelected?.value
    ? getCompanyById(companySelected.value)
    : null;

  const companyDisplayName =
    getLocalizedText(currentCompany?.name, isArabic) ||
    companySelected?.name ||
    "";

  const commercialRegisterNumber =
    currentCompany?.commercialRegisteration || "";

  const taxNumber = currentCompany?.vatNumber || "";

  const setField = useCallback((field, value) => {
    dispatch({ type: "SET_FIELD", field, value });
  }, []);

  const clearValidationFor = useCallback((fields) => {
    setValidationError((current) =>
      fields.includes(current.field) ? EMPTY_VALIDATION : current,
    );
  }, []);

  const accountLookup = useMemo(() => {
    const lookup = new Map();

    walkAccounts(accounts, (account) => {
      const accountNumber = getAccountNumber(account);

      if (accountNumber) {
        lookup.set(accountNumber, account);
      }
    });

    return lookup;
  }, [accounts]);

  const resolveAccountLabel = useCallback(
    (accountNumber) => {
      const normalizedAccountNumber = String(accountNumber || "").trim();

      if (!normalizedAccountNumber) return "";

      return getAccountName(
        accountLookup.get(normalizedAccountNumber),
        isArabic,
      );
    },
    [accountLookup, isArabic],
  );

  useEffect(() => {
    if (!state.account_no) {
      if (state.accountLabel) {
        setField("accountLabel", "");
      }

      return;
    }

    const nextLabel = resolveAccountLabel(state.account_no);

    if (nextLabel !== state.accountLabel) {
      setField("accountLabel", nextLabel);
    }
  }, [resolveAccountLabel, setField, state.accountLabel, state.account_no]);

  useEffect(() => {
    const updatePeriodData = () => {
      try {
        const rawPeriod = localStorage.getItem("selectedPeriod");
        const storedPeriod = rawPeriod ? JSON.parse(rawPeriod) : null;

        if (!storedPeriod) return;

        setSelectedP(storedPeriod);

        if (storedPeriod.startDateDisplay) {
          setField("date_from", storedPeriod.startDateDisplay);
        }

        if (storedPeriod.endDateDisplay) {
          setField("date_to", storedPeriod.endDateDisplay);
        }
      } catch (error) {
        setSelectedP(null);
      }
    };

    updatePeriodData();

    window.addEventListener("storage", updatePeriodData);
    window.addEventListener("periodChanged", updatePeriodData);

    return () => {
      window.removeEventListener("storage", updatePeriodData);
      window.removeEventListener("periodChanged", updatePeriodData);
    };
  }, [setField]);

  const handleManualAccountNumberInput = useCallback(
    (inputValue) => {
      const accountNumber = String(inputValue || "").trim();

      setField("account_no", accountNumber);
      setField("accountLabel", resolveAccountLabel(accountNumber));
      clearValidationFor(["account_no"]);
    },
    [clearValidationFor, resolveAccountLabel, setField],
  );

  const handleSelectAccount = useCallback(
    (account) => {
      const accountNumber = getAccountNumber(account);

      setField("account_no", accountNumber);
      setField(
        "accountLabel",
        getAccountName(account, isArabic) || resolveAccountLabel(accountNumber),
      );
      clearValidationFor(["account_no"]);
    },
    [clearValidationFor, isArabic, resolveAccountLabel, setField],
  );

  const getData = async () => {
    if (!state.date_from) {
      setValidationError({
        field: "date_from",
        message: `${t("reports.start_date")} ${t("common.required")}`.trim(),
      });
      return;
    }

    if (!state.date_to) {
      setValidationError({
        field: "date_to",
        message: `${t("reports.end_date")} ${t("common.required")}`.trim(),
      });
      return;
    }

    const fromDate = new Date(state.date_from);
    const toDate = new Date(state.date_to);

    if (toDate < fromDate) {
      setValidationError({
        field: "date_range",
        message: t("reports.endDateBeforeStart"),
      });
      return;
    }

    const accountNumber = String(state.account_no || "").trim();

    if (!accountNumber) {
      setValidationError({
        field: "account_no",
        message: `${t("reports.accountNumber")} ${t("common.required")}`.trim(),
      });
      return;
    }

    setValidationError(EMPTY_VALIDATION);

    const payload = {
      date_from: state.date_from,
      date_to: state.date_to,
      account_no: accountNumber,
      fin_year_id: selectedP?.id,
      branch_id: branchSelected?.value,
      tenant_id: tenantId,
      company_id: companySelected?.value,
    };

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/reports/rep-01`,
        payload,
      );
      const reportPayload = getReportPayload(response?.data);

      setData(getReportTransactions(reportPayload));
      setOpeningBalance(reportPayload?.opening_balance || 0);
      setHasFetched(true);
    } catch (error) {
      setHasFetched(false);
      setValidationError({
        field: "",
        message: t("reports.errorFetching"),
      });
    } finally {
      setLoading(false);
    }
  };

  const processedData = useMemo(
    () => flattenTransactionDetails(data, state.account_no),
    [data, state.account_no],
  );

  const formatNumber = useCallback(
    (num) => {
      const numericValue = toNumber(num);
      const formattedValue = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Math.abs(numericValue));

      return numericValue < 0 ? `(${formattedValue})` : formattedValue;
    },
    [],
  );

  const resolvedAccountLabel = useMemo(
    () => state.accountLabel || resolveAccountLabel(state.account_no),
    [resolveAccountLabel, state.accountLabel, state.account_no],
  );

  const accountDisplay = state.account_no
    ? resolvedAccountLabel
      ? `${state.account_no} - ${resolvedAccountLabel}`
      : state.account_no
    : EMPTY_VALUE;

  const periodMovementTotals = useMemo(
    () =>
      processedData.reduce(
        (totals, row) => ({
          debit: totals.debit + toNumber(row.balance_de),
          credit: totals.credit + toNumber(row.balance_cr),
        }),
        { debit: 0, credit: 0 },
      ),
    [processedData],
  );

  const periodMovementValue = useMemo(
    () =>
      Math.abs(
        toNumber(periodMovementTotals.debit) -
          toNumber(periodMovementTotals.credit),
      ),
    [periodMovementTotals.credit, periodMovementTotals.debit],
  );


  const closingBalanceValue = useMemo(
    () => toNumber(openingBalance) - periodMovementValue,
    [openingBalance, periodMovementValue],
  );

  const summaryRows = useMemo(
    () => [
      {
        key: "period-movement",
        label: t("reports.periodMovement"),
        debit: periodMovementTotals.debit,
        credit: periodMovementTotals.credit,
        balance: periodMovementValue,
      },

      {
        key: "opening-balance",
        label: t("reports.periodStartBalance"),
        balance: toNumber(openingBalance),
      },
      {
        key: "final-balance",
        label: t("reports.periodFinalBalance"),
        balance: closingBalanceValue,
      },
    ],
    [
      closingBalanceValue,
      openingBalance,
      periodMovementTotals,
      periodMovementValue,
      t,
    ],
  );

  const handleExportExcel = () => {
    if (!hasFetched) return;

    const toExcelNumber = (value) => Number(toNumber(value).toFixed(2));
    const printableSummaryRows = summaryRows.filter(Boolean);
    const headerRows = [
      [reportTitle],
      [companyDisplayName],
      [
        `${t("setting.commercialRegisterNumber")}: ${
          commercialRegisterNumber || EMPTY_VALUE
        }`,
      ],
      [`${t("setting.vatNumber")}: ${taxNumber || EMPTY_VALUE}`],
      [`${t("reports.accountName")}: ${accountDisplay}`],
      [
        t("reports.periodStartDate"),
        state.date_from || EMPTY_VALUE,
        t("reports.periodEndDate"),
        state.date_to || EMPTY_VALUE,
        t("reports.previousBalance"),
        toExcelNumber(openingBalance),
      ],
      [],
    ];

    const headers = [
      serialLabel,
      transactionDateLabel,
      descriptionLabel,
      t("reports.debit"),
      t("reports.credit"),
      balanceLabel,
    ];

    const rows = processedData.map((row) => [
      row.serial || EMPTY_VALUE,
      row.tr_date || EMPTY_VALUE,
      row.description || EMPTY_VALUE,
      toExcelNumber(row.balance_de),
      toExcelNumber(row.balance_cr),
      toExcelNumber(row.movement_balance),
    ]);

    const footerRows = printableSummaryRows.map((row) => [
      "",
      "",
      row.label,
      row.debit !== undefined ? toExcelNumber(row.debit) : "",
      row.credit !== undefined ? toExcelNumber(row.credit) : "",
      row.balance !== undefined ? toExcelNumber(row.balance) : "",
    ]);

    const worksheetData = [...headerRows, headers, ...rows, ...footerRows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const headerRowNumber = headerRows.length + 1;
    const dataStartRowNumber = headerRowNumber + 1;
    const footerStartRowNumber = dataStartRowNumber + rows.length;

    if (isArabic) {
      worksheet["!dir"] = "rtl";
    }

    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 5 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: 5 } },
      { s: { r: 4, c: 0 }, e: { r: 4, c: 5 } },
    ];

    worksheet["!cols"] = [
      { wch: 16 },
      { wch: 14 },
      { wch: 42 },
      { wch: 14 },
      { wch: 14 },
      { wch: 16 },
    ];

    worksheet["!rows"] = worksheetData.map((_, index) => ({
      hpt: index === 0 ? 26 : index >= 1 && index <= 5 ? 22 : 20,
    }));

    worksheet["!autofilter"] = {
      ref: `A${headerRowNumber}:F${headerRowNumber}`,
    };

    const borderStyle = {
      top: { style: "thin", color: { rgb: "D7DCE5" } },
      bottom: { style: "thin", color: { rgb: "D7DCE5" } },
      left: { style: "thin", color: { rgb: "D7DCE5" } },
      right: { style: "thin", color: { rgb: "D7DCE5" } },
    };

    const setCellStyle = (cellRef, style, numberFormat) => {
      const cell = worksheet[cellRef];

      if (!cell) return;

      if (style) {
        cell.s = style;
      }

      if (numberFormat) {
        cell.z = numberFormat;
      }
    };

    setCellStyle("A1", {
      alignment: { horizontal: "center", vertical: "center" },
      font: { bold: true, sz: 16, color: { rgb: "1E3A8A" } },
      fill: { patternType: "solid", fgColor: { rgb: "EFF6FF" } },
      border: borderStyle,
    });

    ["A2", "A3", "A4", "A5"].forEach((cellRef) => {
      setCellStyle(cellRef, {
        alignment: {
          horizontal: isArabic ? "right" : "left",
          vertical: "center",
        },
        font: { bold: cellRef === "A2", color: { rgb: "334155" } },
        fill: { patternType: "solid", fgColor: { rgb: "F8FAFC" } },
        border: borderStyle,
      });
    });

    ["A6", "B6", "C6", "D6", "E6", "F6"].forEach((cellRef, index) => {
      setCellStyle(
        cellRef,
        {
          alignment: {
            horizontal: "center",
            vertical: "center",
            wrapText: true,
          },
          font: { bold: index % 2 === 0, color: { rgb: "334155" } },
          fill: {
            patternType: "solid",
            fgColor: { rgb: index % 2 === 0 ? "EFF6FF" : "FFFFFF" },
          },
          border: borderStyle,
        },
        cellRef === "F6" ? EXCEL_NUMBER_FORMAT : undefined,
      );
    });

    ["A", "B", "C", "D", "E", "F"].forEach((column) => {
      setCellStyle(`${column}${headerRowNumber}`, {
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        font: { bold: true, color: { rgb: "261759" } },
        fill: { patternType: "solid", fgColor: { rgb: "F1F5F9" } },
        border: borderStyle,
      });
    });

    rows.forEach((_, rowIndex) => {
      const excelRowNumber = dataStartRowNumber + rowIndex;

      ["A", "B", "C", "D", "E", "F"].forEach((column) => {
        setCellStyle(
          `${column}${excelRowNumber}`,
          {
            alignment: {
              horizontal:
                column === "C" ? (isArabic ? "right" : "left") : "center",
              vertical: "center",
              wrapText: true,
            },
            font: { color: { rgb: "334155" } },
            border: borderStyle,
          },
          ["D", "E", "F"].includes(column) ? EXCEL_NUMBER_FORMAT : undefined,
        );
      });
    });

    footerRows.forEach((_, rowIndex) => {
      const excelRowNumber = footerStartRowNumber + rowIndex;
      const isLastRow = rowIndex === footerRows.length - 1;

      ["A", "B", "C", "D", "E", "F"].forEach((column) => {
        setCellStyle(
          `${column}${excelRowNumber}`,
          {
            alignment: {
              horizontal:
                column === "C" ? (isArabic ? "right" : "left") : "center",
              vertical: "center",
              wrapText: true,
            },
            font: { bold: true, color: { rgb: "261759" } },
            fill: {
              patternType: "solid",
              fgColor: { rgb: isLastRow ? "E2E8F0" : "F8FAFC" },
            },
            border: borderStyle,
          },
          ["D", "E", "F"].includes(column)
            ? EXCEL_NUMBER_FORMAT
            : undefined,
        );
      });
    });

    const workbook = XLSX.utils.book_new();
    workbook.Workbook = { Views: [{ RTL: isArabic }] };
    XLSX.utils.book_append_sheet(workbook, worksheet, reportTitle);
    XLSX.writeFile(
      workbook,
      `${exportBaseName}_${state.account_no || "account"}_${state.date_from}_to_${state.date_to}.xlsx`,
    );
  };

  const handlePrint = () => {
    if (!hasFetched) {
      window.alert(t("reports.noDataPrint"));
      return;
    }

    const printWindow = window.open("", "", "width=1400,height=900");

    if (!printWindow) return;

    const printableSummaryRows = summaryRows.filter(Boolean);
    const printHeaderHtml = `
      <div class="header">
        <h1>${reportTitle}</h1>
        
        <div class="header-company-section">
          <div class="header-company-name">${companyDisplayName}</div>
          <div class="header-company-details">
            ${commercialRegisterNumber ? `<span>${t("setting.commercialRegisterNumber")}: ${commercialRegisterNumber}</span>` : ""}
            ${taxNumber ? `<span>${t("setting.vatNumber")}: ${taxNumber}</span>` : ""}
          </div>
        </div>
        
        <div class="header-info">
          <span>${t("reports.periodStartDate")}: ${state.date_from || EMPTY_VALUE}</span>
          <span>${t("reports.periodEndDate")}: ${state.date_to || EMPTY_VALUE}</span>
        </div>
        
        <div class="header-account-section">
          <span>${t("reports.accountName")}: ${accountDisplay}</span>
          ${toNumber(openingBalance) !== 0 ? `<span>${t("reports.previousBalance")}: ${formatNumber(openingBalance)}</span>` : ""}
        </div>
      </div>
    `;

    const tableRowsHtml = processedData
      .map(
        (row, index) => `
          <tr class="statement-row ${index % 2 === 0 ? "row-even" : "row-odd"}">
            <td>${row.serial || EMPTY_VALUE}</td>
            <td>${row.tr_date || EMPTY_VALUE}</td>
            <td class="statement-cell">${row.description || EMPTY_VALUE}</td>
            <td>${formatNumber(row.balance_de)}</td>
            <td>${formatNumber(row.balance_cr)}</td>
            <td class="amount-balance amount-balance-neutral">${formatNumber(row.movement_balance)}</td>
          </tr>
        `,
      )
      .join("");

    const summaryRowsHtml = printableSummaryRows
      .map(
        (row) => `
          <tr class="summary-row">
            <td class="summary-empty-cell"></td>
            <td class="summary-empty-cell"></td>
            <td class="summary-label-cell">${row.label}</td>
            <td class="summary-debit-cell">${row.debit !== undefined ? formatNumber(row.debit) : ""}</td>
            <td class="summary-credit-cell">${row.credit !== undefined ? formatNumber(row.credit) : ""}</td>
            <td class="summary-balance-cell">${row.balance !== undefined ? formatNumber(row.balance) : ""}</td>
          </tr>
        `,
      )
      .join("");

    const printFooterHtml = `
      <div class="report-footer">
        <img class="report-footer-logo" src="/cube.JPG" alt="${companyDisplayName}" />
        <div class="footer-website">${t("reports.printFooterWebsite")}</div>
      </div>
    `;

    const htmlContent = `
<!DOCTYPE html>
<html dir="${isArabic ? "rtl" : "ltr"}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${reportTitle}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }

    body {
      font-family: Arial, sans-serif;
      direction: ${isArabic ? "rtl" : "ltr"};
      margin: 10px;
      color: #000;
      background: #fff;
    }

    .report-shell {
      min-height: calc(100vh - 20px);
      padding-bottom: 72px;
    }

    .report-content {
      min-height: 100%;
    }

    .header {
      text-align: center;
      margin-bottom: 15px;
    }

    .header h1 {
      font-size: 16px;
      font-weight: 700;
      color: #000;
      margin-bottom: 8px;
    }

    .header-info {
      display: flex;
      justify-content: space-between;
      gap: 10px;
      font-size: 10px;
    }

    .header-company-section {
      margin: 10px 0 8px 0;
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 6px;
      background-color: #f9f9f9;
    }

    .header-company-name {
      font-size: 12px;
      font-weight: bold;
      color: #1a1a1a;
      margin-bottom: 4px;
    }

    .header-company-details {
      display: flex;
      justify-content: center;
      gap: 15px;
      font-size: 9px;
      color: #333;
      flex-wrap: wrap;
    }

    .header-company-details span {
      margin: 0 2px;
    }

    .header-account-section {
      display: flex;
      justify-content: space-between;
      gap: 10px;
      font-size: 10px;
      margin-top: 6px;
      flex-wrap: wrap;
    }

    .table-shell {
      margin-top: 10px;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }

    thead {
      display: table-header-group;
    }

    th, td {
      border: 1px solid #333;
      padding: 5px 4px;
      text-align: center;
      font-size: 9px;
      color: #000;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    th {
      font-weight: 700;
      text-align: ${isArabic ? "center" : "center"};
    }

    .statement-cell {
      text-align: ${isArabic ? "right" : "left"};
      line-height: 1.6;
      color: #000;
    }

    .amount-balance {
      font-weight: 700;
      color: #000;
    }

    .summary-row td {
      font-weight: 700;
    }

    .summary-label-cell {
      text-align: ${isArabic ? "right" : "left"};
      padding-${isArabic ? "right" : "left"}: 12px;
    }

    .summary-empty-cell {
      background: inherit;
    }

    .summary-debit-cell {
      background: inherit !important;
    }

    .summary-credit-cell {
      background: inherit !important;
    }

    .summary-balance-cell {
      background: inherit !important;
      color: #000;
    }

    .report-footer {
      position: fixed;
      left: 10px;
      right: 10px;
      bottom: 8px;
      padding-top: 6px;
      border-top: 1px solid #cfd8e3;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      background: #fff;
    }

    .report-footer-logo {
      width: 52px;
      max-width: 52px;
      height: auto;
      object-fit: contain;
      display: block;
    }

    .footer-website {
      color: #000;
      font-size: 10px;
      letter-spacing: 0.4px;
      text-align: center;
      line-height: 1.1;
    }

    @media print {
      body {
        margin: 5px;
        font-size: 9px;
        background: #fff;
      }

      .report-shell {
        min-height: calc(100vh - 10px);
        padding-bottom: 64px;
      }

      .report-content {
        min-height: 100%;
      }

      table {
        page-break-inside: avoid;
        margin-top: 5px;
      }

      tr {
        page-break-inside: avoid;
        page-break-after: auto;
      }
    }
  </style>
</head>
<body>
  <div class="report-shell">
    <div class="report-content">
      <div class="report-main">
        ${printHeaderHtml}

        <div class="table-shell">
          <table>
            <colgroup>
              <col style="width:12%" />
              <col style="width:16%" />
              <col style="width:34%" />
              <col style="width:12%" />
              <col style="width:12%" />
              <col style="width:14%" />
            </colgroup>
            <thead>
              <tr>
                <th>${serialLabel}</th>
                <th>${transactionDateLabel}</th>
                <th style="text-align:${isArabic ? "right" : "left"};">${descriptionLabel}</th>
                <th>${t("reports.debit")}</th>
                <th>${t("reports.credit")}</th>
                <th>${balanceLabel}</th>
              </tr>
            </thead>
            <tbody>
              ${tableRowsHtml}
              ${summaryRowsHtml}
            </tbody>
          </table>
        </div>
      </div>
      ${printFooterHtml}
    </div>
  </div>
</body>
</html>`;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const dateErrorMessage =
    validationError.field === "date_from" ||
    validationError.field === "date_to" ||
    validationError.field === "date_range"
      ? validationError.message
      : "";

  const accountErrorMessage =
    validationError.field === "account_no" ? validationError.message : "";

  const generalErrorMessage =
    validationError.field === "" ? validationError.message : "";

  return (
    <section className="space-y-4 p-1 font-normal">
      <CardHeadeWithActions
        title={reportTitle}
        icon={"/assets/icons/balance.png"}
      >
        {hasFetched && (
          <div className="flex gap-2">
            <MwButton
              children={t("reports.print")}
              onClick={handlePrint}
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
            />
            <MwButton
              children={t("reports.exportExcel")}
              onClick={handleExportExcel}
              className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
            />
          </div>
        )}
      </CardHeadeWithActions>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_14px_40px_-34px_rgba(15,23,42,0.45)] sm:p-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5 xl:items-end">
          <div className="xl:col-span-1">
            <MwInputText
              value={state.date_from}
              onChange={(event) => {
                setField("date_from", event.target.value);
                clearValidationFor(["date_from", "date_range"]);
              }}
              invalid={Boolean(dateErrorMessage)}
              invalidMsg={dateErrorMessage}
              inputType="date"
              label={t("reports.start_date")}
            />
          </div>
          <div className="xl:col-span-1">
            <MwInputText
              value={state.date_to}
              onChange={(event) => {
                setField("date_to", event.target.value);
                clearValidationFor(["date_to", "date_range"]);
              }}
              invalid={Boolean(dateErrorMessage)}
              invalidMsg={dateErrorMessage}
              inputType="date"
              label={t("reports.end_date")}
            />
          </div>
          <div className="xl:col-span-2">
            <SelectorTree
              allowManualNumberInput={true}
              headerTitle={t("reports.accountName")}
              classNameInput="text-center"
              inlineSuggestionsPosition="bottom"
              withInlineSuggestions
              allowedAccountTypes={["secondary"]}
              value={state.account_no}
              label={state.accountLabel}
              data={accounts}
              title={reportTitle}
              placeholderNumber={t("reports.accountNumber")}
              placeholderName={t("reports.accountName")}
              onManualNumberChange={handleManualAccountNumberInput}
              onSelect={handleSelectAccount}
              invalid={Boolean(accountErrorMessage)}
              invalidMsg={accountErrorMessage}
            />
          </div>
          <div className="flex items-end md:col-span-2 xl:col-span-1">
            <MwButton
              children={
                loading ? <MwSpinnerButton /> : t("reports.generateReport")
              }
              onClick={getData}
              className="w-full xl:w-auto"
            />
          </div>
        </div>

        {generalErrorMessage && (
          <p className="mt-3 text-xs text-red-500">{generalErrorMessage}</p>
        )}
      </div>

      {hasFetched && (
        <div
          ref={tableRef}
          className="relative mt-5 overflow-hidden rounded-[28px] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] p-5 shadow-[0_28px_70px_-42px_rgba(15,23,42,0.45)] sm:p-6 lg:p-7"
        >

        

          <div className="relative   ">
            <div className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
              <div className="overflow-auto">
                <table
                  className="min-w-[920px] w-full border-collapse text-[13px] text-[#261759]"
                  style={{ tableLayout: "fixed" }}
                >
                  <colgroup>
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "34%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "14%" }} />
                  </colgroup>

                  <thead className="bg-slate-100 text-[#261759]">
                    <tr className="text-center border-b-2 border-gray-300">
                      <th className="border p-2 align-middle font-bold">
                        {serialLabel}
                      </th>
                      <th className="border p-2 align-middle font-bold">
                        {transactionDateLabel}
                      </th>
                      <th className="border p-2 text-start align-middle font-bold">
                        {descriptionLabel}
                      </th>
                      <th className="border p-2 align-middle font-bold">
                        {t("reports.debit")}
                      </th>
                      <th className="border p-2 align-middle font-bold">
                        {t("reports.credit")}
                      </th>
                      <th className="border p-2 align-middle font-bold">
                        {balanceLabel}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {processedData.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b text-center text-[#261759] hover:bg-gray-50"
                      >
                        <td className="border text-[#261759] p-2 font-medium">
                          {row.serial || EMPTY_VALUE}
                        </td>
                        <td className="border p-2 font-medium text-[#261759]">
                          {row.tr_date || EMPTY_VALUE}
                        </td>
                        <td className="border p-2 text-start leading-6 text-[#261759]">
                          {row.description || EMPTY_VALUE}
                        </td>
                        <td className="border text-[#261759] p-2">
                          {formatNumber(row.balance_de)}
                        </td>
                        <td className="border text-[#261759] p-2">
                          {formatNumber(row.balance_cr)}
                        </td>
                        <td className="border p-2 font-semibold text-[#261759]">
                          {formatNumber(row.movement_balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>

                  <tfoot>
                    {summaryRows.map((row, index) => (
                      <tr
                        key={row.key}
                        className={`text-center font-bold text-[#261759] ${
                          index === summaryRows.length - 1
                            ? "bg-slate-100"
                            : "bg-slate-50"
                        }`}
                      >
                        <td className="border p-2" />
                        <td className="border p-2" />
                        <td className="border p-2 text-start">{row.label}</td>
                        <td className="border bg-blue-50 p-2">
                          {row.debit !== undefined
                            ? formatNumber(row.debit)
                            : ""}
                        </td>
                        <td className="border bg-slate-50 p-2">
                          {row.credit !== undefined
                            ? formatNumber(row.credit)
                            : ""}
                        </td>
                        <td className="border bg-blue-50 p-2 text-[#261759]">
                          {row.balance !== undefined
                            ? formatNumber(row.balance)
                            : ""}
                        </td>
                      </tr>
                    ))}
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
